import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import api from "../api/client";

/**
 * Appliance license state for the tech UI. Polls /api/v1/license/status (an
 * always-open endpoint, so it answers even while the appliance is locked) and
 * also listens for the `license:locked` / `license:refresh` window events that
 * the axios 402 interceptor emits — so a blocked request flips us into the lock
 * screen immediately, without waiting for the next poll.
 *
 * `hasFeature` mirrors the backend rule (services/licensing.feature_enabled):
 * unlicensed / trial / empty-feature licenses ⇒ everything on; a non-empty
 * feature list restricts to the listed features. Layout uses it to hide nav
 * entries for modules this license doesn't include.
 */

export type LicenseStatus = {
  status: string;
  licensed: boolean;
  tier?: string | null;
  features?: string[];
  read_only?: boolean;
  locked?: boolean;
};

type LicenseCtx = {
  lic: LicenseStatus | null;
  locked: boolean;
  lockStatus: string | null;
  hasFeature: (feature: string) => boolean;
  refresh: () => void;
};

// Statuses that mean "hard locked" (the backend 402s everything but the open
// prefixes). Kept in sync with services/licensing.is_locked.
const LOCK_STATUSES = new Set(["expired", "revoked", "trial_expired"]);

const Ctx = createContext<LicenseCtx>({
  lic: null,
  locked: false,
  lockStatus: null,
  hasFeature: () => true,
  refresh: () => {},
});

export const useLicense = () => useContext(Ctx);

export function LicenseProvider({ children }: { children: React.ReactNode }) {
  const [lic, setLic] = useState<LicenseStatus | null>(null);
  const [lockStatus, setLockStatus] = useState<string | null>(null);

  const refresh = useCallback(() => {
    // No token → not logged in (e.g. /login). Skip; the status endpoint needs
    // auth and would just 401 → bounce to login.
    if (!localStorage.getItem("token")) return;
    api
      .get("/api/v1/license/status")
      .then((r) => {
        const data = r.data as LicenseStatus;
        setLic(data);
        setLockStatus(LOCK_STATUSES.has(data?.status) ? data.status : null);
      })
      .catch(() => {
        /* Keep prior state on a transient error; a real 401 redirects via the
           client interceptor. Never trap the operator on a blip. */
      });
  }, []);

  useEffect(() => {
    refresh();
    const poll = setInterval(refresh, 60000);

    const onLocked = (e: Event) => {
      const s = (e as CustomEvent).detail?.status as string | undefined;
      setLockStatus(s && LOCK_STATUSES.has(s) ? s : "expired");
    };
    const onRefresh = () => refresh();
    window.addEventListener("license:locked", onLocked);
    window.addEventListener("license:refresh", onRefresh);
    return () => {
      clearInterval(poll);
      window.removeEventListener("license:locked", onLocked);
      window.removeEventListener("license:refresh", onRefresh);
    };
  }, [refresh]);

  const hasFeature = useCallback(
    (feature: string) => {
      const features = lic?.features || [];
      return features.length === 0 || features.includes(feature);
    },
    [lic],
  );

  return (
    <Ctx.Provider value={{ lic, locked: lockStatus !== null, lockStatus, hasFeature, refresh }}>
      {children}
    </Ctx.Provider>
  );
}
